'use client'
import { FormControl, FormItem, FormLabel, FormMessage } from '../form/form'
import { TypeOf, ZodSchema } from 'zod'
import { ControllerRenderProps, Path } from 'react-hook-form'
import { ChevronRight } from 'lucide-react'
import { FormMode } from '@/lib/common-models'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../dialog/dialog'
import { Button } from '../button/button'
import { RadioGroup, RadioGroupItem } from '../radio-button/radio-button'
import React from 'react'
import { HiddenInput } from '@/components/ui/fields/hidden-input'
import { cn } from '@/lib/utils'

// TO-DO: Implementing Dialog Directly here is a hack for a quick fix, ideally mode would be passed as context through parent FormField and managed there

export interface Option {
  value: string
  label: string
  disabled?: boolean
}

interface RadioButtonInputProps<
  T extends ZodSchema,
  K extends Path<TypeOf<T>>,
> {
  field: ControllerRenderProps<TypeOf<T>, K>
  mode: FormMode
  options: Option[]
  label: string
  labelClassName?: string
  className?: string
  radioGroupClassName?: string
  optionClassName?: string
  /**
   * Transaction name for RUM monitoring.
   * Required for observability - tracks user interactions in Elastic APM.
   * Format: area.page.field_name (e.g., "forms.fd02.account_type")
   */
  transactionName: string
}

export const RadioButtonInput = <
  T extends ZodSchema,
  K extends Path<TypeOf<T>>,
>({
  field,
  label,
  options,
  mode,
  labelClassName,
  className,
  radioGroupClassName,
  optionClassName,
  transactionName,
}: RadioButtonInputProps<T, K>) => {
  const setAttribute = (value: string) => {
    field.onChange(value)
  }
  const renderCreateMode = () => {
    return (
      <FormItem className="animate-fade-in space-y-3 pt-6">
        <FormLabel
          className={cn('text-[24px] font-semibold text-black', labelClassName)}
        >
          {label}
        </FormLabel>
        <FormControl>
          <RadioGroup
            className={cn(
              'flex flex-col sm:flex-row lg:gap-4',
              className,
              radioGroupClassName
            )}
            onValueChange={setAttribute}
            value={field.value}
            name={field.name}
          >
            {options.map((option) => (
              <FormItem
                key={option.value}
                className={cn(`flex items-center space-x-1 space-y-4`)}
              >
                <FormControl>
                  <RadioGroupItem
                    value={option.value}
                    disabled={option.disabled ?? false}
                    transactionName={transactionName}
                    className={'shadow-none'}
                  />
                </FormControl>
                <FormLabel
                  className={`m-0 mt-4 p-0 pt-1 text-white ${optionClassName}`}
                >
                  {option.label}
                </FormLabel>
              </FormItem>
            ))}
          </RadioGroup>
        </FormControl>
        <FormMessage />
      </FormItem>
    )
  }

  const renderViewMode = () => {
    return (
      <div
        className={cn(
          'm-3 flex size-fit flex-col rounded-3xl py-3 md:m-4',
          className
        )}
      >
        {' '}
        <h4
          className={cn(
            'font-medium text-muted-foreground',
            labelClassName ?? 'text-[24px] font-medium text-muted-foreground'
          )}
        >
          {label}
        </h4>
        <h3 className="py-2 text-sm font-semibold">
          {options
            .filter((option) => option.value === field.value)
            .map((option) => option.label)
            .join(', ')}
        </h3>
        <HiddenInput
          name={field.name}
          transactionName={`components.${field.name}`}
        />
      </div>
    )
  }

  const renderUpdateDialog = () => {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button
            variant="linkHover2"
            className="ml-12 font-bold text-blue-700"
            transactionName={`${transactionName}.edit`}
          >
            Edit
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px] md:max-w-[875px]">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
          </DialogHeader>
          {renderCreateMode()}
          <DialogFooter>
            <DialogClose>
              <Button
                className="w-96"
                type="submit"
                variant="expandIcon"
                iconPlacement="right"
                Icon={ChevronRight}
                transactionName={`${transactionName}.continue`}
              >
                Continue
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  const renderButtons = () => {
    switch (mode) {
      case FormMode.CREATE:
        return renderCreateMode()
      case FormMode.READ:
        return renderViewMode()
      case FormMode.UPDATE:
        return renderUpdateDialog()
      default:
        return <></>
    }
  }

  return <>{renderButtons()}</>
}
